import { useState } from "react";
import { Newspaper } from "lucide-react";
import { Button } from "@/components/ui/button";
import MediaCard from "@/components/MediaCard";
import { mediaArticles, mediaCategories, type MediaCategory } from "@/data/mediaArticles";

const MediaCoverage = () => {
  const [activeCategory, setActiveCategory] = useState<MediaCategory | "all">("all");

  const filteredArticles = activeCategory === "all"
    ? mediaArticles
    : mediaArticles.filter((article) => article.category === activeCategory);

  const featuredArticle = mediaArticles.find((a) => a.imageUrl);

  return (
    <div className="py-12">
      {/* Section Header */}
      <div className="text-center mb-10">
        <div className="inline-flex items-center gap-2 bg-secondary/10 rounded-full px-4 py-2 mb-4">
          <Newspaper className="h-5 w-5 text-secondary" />
          <span className="font-body font-semibold text-secondary">Media Coverage</span>
        </div>
        <h2 className="font-display text-3xl md:text-4xl font-bold text-foreground mb-3">
          Matt in the News
        </h2>
        <p className="font-body text-muted-foreground max-w-2xl mx-auto">
          Read what local and state media are saying about Matt's work on the school board and his campaign for the 33rd District.
        </p>
      </div>

      {/* Category Filters */}
      <div className="flex flex-wrap justify-center gap-2 mb-8" role="group" aria-label="Filter articles by category">
        <Button
          variant={activeCategory === "all" ? "default" : "outline"}
          size="sm"
          onClick={() => setActiveCategory("all")}
          aria-pressed={activeCategory === "all"}
          className="rounded-full"
        >
          All Coverage
        </Button>
        {mediaCategories.map((category) => (
          <Button
            key={category}
            variant={activeCategory === category ? "default" : "outline"}
            size="sm"
            onClick={() => setActiveCategory(category)}
            aria-pressed={activeCategory === category}
            className="rounded-full"
          >
            {category}
          </Button>
        ))}
      </div>

      {/* Featured Article (when showing all) */}
      {activeCategory === "all" && featuredArticle && (
        <div className="mb-8 max-w-2xl mx-auto">
          <MediaCard article={featuredArticle} />
        </div>
      )}

      {/* Articles Grid */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {filteredArticles
          .filter((article) => activeCategory !== "all" || article.id !== featuredArticle?.id)
          .map((article) => (
            <MediaCard key={article.id} article={article} />
          ))}
      </div>

      {/* Empty State */}
      {filteredArticles.length === 0 && (
        <div className="text-center py-12">
          <Newspaper className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <p className="font-body text-muted-foreground">No articles found in this category.</p>
        </div>
      )}
    </div>
  );
};

export default MediaCoverage;
